/**
 * AuthProvider Component
 * คอมโพเนนต์ผู้ให้บริการ Auth
 *
 * Handles token refresh on app mount and window focus.
 * จัดการการรีเฟรช token เมื่อแอปโหลดและเมื่อหน้าต่างได้รับโฟกัส
 */

import { useEffect, useRef, type ReactNode } from 'react'
import { useAuthStore } from '@/features/auth/auth.store'
import { authApi } from '@/features/auth/auth.api'
import { isTokenExpired } from '@/features/auth/token.logic'

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { login, logout, setLoading, accessToken } = useAuthStore()
  const isInitialized = useRef(false)

  // Initial auth check on mount — try to refresh token from cookie
  useEffect(() => {
    if (isInitialized.current) return
    isInitialized.current = true

    const initAuth = async () => {
      setLoading(true)
      try {
        const response = await authApi.refreshToken()
        login(response.accessToken, response.user)
      } catch {
        // No valid session — user needs to login
        logout()
      }
    }

    void initAuth()
  }, [login, logout, setLoading])

  // Refresh token on window focus (if near expiry)
  useEffect(() => {
    const handleFocus = async () => {
      const token = useAuthStore.getState().accessToken
      if (!token) return

      // Only refresh if token is near expiry (within 2 minutes)
      if (isTokenExpired(token, 120)) {
        try {
          const response = await authApi.refreshToken()
          useAuthStore.getState().login(response.accessToken, response.user)
        } catch {
          useAuthStore.getState().logout()
        }
      }
    }

    const onFocus = () => {
      void handleFocus()
    }
    window.addEventListener('focus', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
    }
  }, [])

  // Background refresh interval (every 10 minutes)
  useEffect(() => {
    const refreshInterval = parseInt(
      (import.meta.env['VITE_TOKEN_REFRESH_INTERVAL'] as string | undefined) ?? '600000',
      10
    )

    const interval = setInterval(() => {
      const token = useAuthStore.getState().accessToken
      if (!token) return

      if (isTokenExpired(token, 120)) {
        authApi
          .refreshToken()
          .then((response) => {
            useAuthStore.getState().login(response.accessToken, response.user)
          })
          .catch(() => {
            useAuthStore.getState().logout()
          })
      }
    }, refreshInterval)

    return () => {
      clearInterval(interval)
    }
  }, [accessToken])

  return <>{children}</>
}
